import { ChangeDetectionStrategy, Component, Inject, Input, OnDestroy, OnInit, Optional, ViewChild } from '@angular/core';
import { IFilterFacet } from '@impact/data';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { chevronDownIcon } from '../../../icons';
import { FacetStateService } from '../../core/facet-state.service';
import { ExpansionPanelComponent } from '../../expansion-panel/expansion-panel.component';
import { FilterService } from '../../filter-service/filter.service';
import { IMultiRangeOutput } from '../../form-elements/multi-range/multi-range.component';
import { COLLAPSE_PANELS } from '../../models/filter.models';

@Component({
    selector: 'app-range-facet',
    template: `
        <div class="facet range-facet">
            <app-expansion-panel #panel [headerClickable]="true" [expanded]="expandPanel" (changed)="onPanelChange($event)">
                <h6 class="facet__title" expansion-title [class.facet__title--no-margin]="!panel.animating && !panel.expanded">
                    {{ facet?.label }}
                </h6>
                <ng-container expansion-icon>${chevronDownIcon}</ng-container>
                <app-input-multi-range
                    [controlledValStart]="controlledValStart"
                    [controlledValEnd]="controlledValEnd"
                    [minVal]="minVal"
                    [maxVal]="maxVal"
                    [inputStepSize]="stepSize"
                    (handleChange)="handleChange($event)"
                ></app-input-multi-range>
            </app-expansion-panel>
        </div>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RangeFacetComponent implements OnInit, OnDestroy {
    @Input() facetKey: string;
    @Input() facet: IFilterFacet;
    @Input() stepSize = 10000;

    expandPanel = true;

    private readonly unsubscribe = new Subject<void>();
    get maxVal() {
        const max = this.facet?.items?.[1]?.count || 0;
        // Calculating a maxvalue that is so high that the stepSize wont make it impossible to reach.
        // example: if max was 95000, and stepSize is 10000, range will not be able to go over 90000. so we round up to 100000.
        return max + (this.stepSize - (max % this.stepSize));
    }
    get minVal() {
        const min = this.facet?.items?.[0]?.count || 0;
        return min - (min % this.stepSize);
    }
    private _activeFacetValue?: string;
    get controlledValStart() {
        const activefacetMinVal = this._activeFacetValue?.split('-')?.[0];
        if (activefacetMinVal) {
            const parsedMin = parseInt(activefacetMinVal);
            const min = isNaN(parsedMin) ? 0 : parsedMin;
            return min < this.minVal ? this.minVal : min;
        } else {
            return this.minVal;
        }
    }

    get controlledValEnd() {
        const activefacetMaxVal = this._activeFacetValue?.split('-')?.[1];
        if (activefacetMaxVal) {
            const parsedMax = parseInt(activefacetMaxVal);
            const max = isNaN(parsedMax) ? 0 : parsedMax;
            return max > this.maxVal ? this.maxVal : max;
        } else {
            return this.maxVal;
        }
    }

    @ViewChild('panel') panel: ExpansionPanelComponent;

    constructor(
        public filterService: FilterService,
        private facetStateService: FacetStateService,
        @Optional() @Inject(COLLAPSE_PANELS) private collapsePanels: boolean
    ) {}

    ngOnInit() {
        this.filterService.activeFacets.pipe(takeUntil(this.unsubscribe)).subscribe((activeFacets) => {
            this._activeFacetValue = activeFacets[this.facetKey]?.[0];
        });

        if (this.facet.label) {
            const expandState = this.facetStateService.getExpandedState(this.facet.label);
            this.expandPanel = expandState ?? this.collapsePanels ?? false;
        }
    }
    handleChange({ from, to }: IMultiRangeOutput) {
        this.filterService.setFacet({ facet: this.facetKey, option: { key: `${from}-${to}` } }, true);
    }

    onPanelChange(value: boolean) {
        if (!this.facet.label) {
            return;
        }

        if (value) {
            this.facetStateService.storeFacetAsExpanded(this.facet.label);
        } else {
            this.facetStateService.storeFacetAsColapsed(this.facet.label);
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
